/**
 * The world layer (decision 0009, visual spec): the plateau as real geometry under an
 * orthographic camera that turns, tilts and zooms. One unit is one tile; tile (x, y) is at
 * (x, height, y), the plateau's top at height 0 and the mist floor at minus 1.8.
 *
 * Frame: a geometry pass into two buffers (tone, kind, height and weight; outline id and
 * coverage), a post pass that draws outlines, mist and halftone to the screen, and a marker pass
 * that draws numbers and banners crisp from the atlas on top.
 */
import { type AtlasDef, CATALOG, familyOf, type ThemeDef } from "@mistsweeper/assets";
import { type Coord, mix32, Terrain } from "@mistsweeper/core";
import {
  BufferAttribute,
  BufferGeometry,
  DataTexture,
  DepthTexture,
  DoubleSide,
  Float32BufferAttribute,
  GLSL3,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  InstancedMesh,
  LinearFilter,
  Matrix4,
  Mesh,
  NearestFilter,
  NoColorSpace,
  OrthographicCamera,
  PlaneGeometry,
  RawShaderMaterial,
  RedFormat,
  RepeatWrapping,
  Texture,
  Scene as ThreeScene,
  UnsignedByteType,
  Vector2,
  Vector3,
  Vector4,
  WebGLRenderer,
  WebGLRenderTarget,
} from "three";
import { FogState, nearness } from "./fog.ts";
import type { Bands, ModelSet } from "./models.ts";
import { makeNoiseTexture, NOISE_SIZE } from "./noise.ts";
import { CREATURES, creatureOn, hasTop, SWAY, WATER_DROP } from "./placement.ts";
import {
  type Attachment,
  DEPTH,
  type Dress,
  FLAG,
  type Instance,
  MOTION_FLAGS,
  PUFF,
  type Scene,
  SceneBuilder,
  type SmokeSource,
  shadowSunDirection,
  sunDirection,
} from "./scene.ts";
import {
  COLUMN_FRAGMENT,
  COLUMN_VERTEX,
  FISH_FRAGMENT,
  FISH_VERTEX,
  GNAT_FRAGMENT,
  GNAT_VERTEX,
  MARKER_FRAGMENT,
  MARKER_VERTEX,
  MODEL_FRAGMENT,
  MODEL_VERTEX,
  OVERLAY_FRAGMENT,
  OVERLAY_VERTEX,
  POST_FRAGMENT,
  POST_VERTEX,
  SHADOW_FRAGMENT,
  TILT_FRAGMENT,
} from "./shaders.ts";
import { type Thing, type TouchSource, thingAt, thingOn } from "./touch.ts";
import type { WorldSource } from "./world-source.ts";

/** Art units per tile: a tile's diagonal is the 48-unit width of the flat renderer's diamond. */
export const ART_PER_TILE = 48 / Math.SQRT2;
/**
 * Device pixels per art unit, continuous between these. The app sets the closest zoom to sixteen
 * times the reference scale, which on a phone is MAX_SCALE.
 */
export const MIN_SCALE = 0.8;
export const MAX_SCALE = 38.4;
/** How far the camera tilts, from the ground up. */
export const MIN_PITCH = (12 * Math.PI) / 180;
export const MAX_PITCH = (72 * Math.PI) / 180;

/**
 * Where the camera looks (spec/formats.md, Views): the point of the plateau in tiles, the turn
 * around the vertical and the tilt above the ground in radians, and the scale in device pixels per
 * art unit.
 */
export interface CameraView {
  readonly x: number;
  readonly y: number;
  readonly yaw: number;
  readonly pitch: number;
  readonly scale: number;
}

/**
 * A flock drawn on the screen, by its leader: its slot, where it flies in the world, in tiles,
 * and when on the clock, in seconds, its leader next starts to beat its wings.
 */
export interface Flock {
  readonly k: number;
  readonly x: number;
  readonly y: number;
  readonly h: number;
  readonly beat: number;
}
/**
 * The window of tiles the scene and the mist textures cover grows with what the camera sees,
 * in steps of 32 tiles, between these sizes.
 */
/**
 * Life, on unless `&life=0`. Flocks of birds cross the sky now and then: time is cut into
 * slots, and in most slots a flock sets out on a straight line through the place the camera was
 * looking at, so it flies across the view and not over some far corner of the world. Gnats dance
 * over the swarms the scene places. Visual spec, Markers.
 */
const FLIGHT_SLOT = 25;
const FLIGHT_CHANCE = 0.65;
const FLIGHT_LENGTH = 72;
const FLIGHT_SPEED = 1.8;
const SALT_FLIGHT = 0x666c6974; // "flit"
const SALT_GNAT = 0x676e6174; // "gnat"
/**
 * A fish's leap: seconds in the air, seconds its rings last, how far it goes and how high, and
 * its length, in tiles. Up to MAX_SPLASHES sets of rings show at once.
 */
const FISH_LEAP = 0.7;
const FISH_RINGS = 3;
const FISH_REACH = 0.3;
const FISH_HEIGHT = 0.16;
const FISH_LENGTH = 0.2;
const MAX_SPLASHES = 16;
/**
 * A duck's rings: a calm set spreads from where it is every this many seconds, at this strength,
 * so it leaves a faint trail as it swims; when it upends and when it comes up, a set at full
 * strength.
 */
const DUCK_CALM = 1.6;
const DUCK_CALM_STRENGTH = 0.3;
/** How far a duck swims from its place, in tiles, and how far round its circle it goes each time. */
const DUCK_RADIUS = 0.16;
const DUCK_ARC = 2.2;

/** A duck's beat: its period in seconds and the share of a turn its swim starts at. */
function duckBeat(x: number, y: number): { beat: number; period: number } {
  return { beat: fract(x * 0.53 + y * 0.29), period: 20 + 6 * fract(x * 0.17 + y * 0.41) };
}

/** Where a duck is at t seconds, as the model shader moves it (FLAG.dabble). */
function duckAt(d: { x: number; y: number; scale: number }, t: number): { x: number; y: number } {
  const { beat, period } = duckBeat(d.x, d.y);
  const cycle = t / period + beat;
  const k = Math.floor(cycle);
  const u = cycle - k;
  const g = Math.min(1, Math.max(0, u / 0.5));
  const theta = beat * TAU + (k + g * g * (3 - 2 * g)) * DUCK_ARC;
  return {
    x: d.x + d.scale * DUCK_RADIUS * Math.cos(theta),
    y: d.y - d.scale * DUCK_RADIUS * Math.sin(theta),
  };
}
/** A bird's shadow, as wide as this share of the bird. */
const BIRD_SHADOW = 0.75;
const TAU = Math.PI * 2;

function fract(v: number): number {
  return v - Math.floor(v);
}
/** The digits and their halos, times their size in the atlas: a little smaller since 2026-10-15. */
const DIGIT_SIZE = 0.85;
/** The tone of a house's interior seen through its openings, unless the theme has its own: dark. */
const GLOW_TONE = 0.15;
/** Seconds a banner taken away still shows, rolling up its cloth and sinking into the mist. */
const BANNER_LEAVE = 0.8;
/** The height a banner's cloth hangs from where its flag has no cloth probe. */
const BANNER_TOP = 0.86;
/** Texels per side of the shadow map. */
const SHADOW_SIZE = 2048;
const MIN_WINDOW = 96;
const MAX_WINDOW = 256;
/** Glide after a pan: how fast it decays, per millisecond. */
const GLIDE_DECAY = 0.0045;

export type DitherLock = "world" | "screen";

export interface FrameStats {
  /** Milliseconds spent building the scene on the CPU, 0 when the cached scene was reused. */
  readonly composeMs: number;
  /** Milliseconds spent updating the mist and issuing the draws. */
  readonly drawMs: number;
  /** True while a reveal fades in or the camera glides, so the next frame should come soon. */
  readonly animating: boolean;
  /**
   * True while birds fly over the screen: they cross it fast enough to step at the 30 frames a
   * second the rest of life gets, so frames come at the full rate while they show (Daniel,
   * 2026-10-21, on the app: the wind and the smoke look the same at 30, the birds step).
   */
  readonly quick: boolean;
}

export interface RendererOptions {
  readonly theme: ThemeDef;
  readonly scale: number;
  readonly dot: number;
  readonly reducedMotion: boolean;
  readonly preserveDrawingBuffer: boolean;
  readonly lock: DitherLock;
  /**
   * Zoomed out, the halftone turns finer: dots 4 device pixels apart instead of 8, blended in
   * as the scale falls from `from` to `to` device pixels per art unit. Null keeps 8 throughout.
   */
  readonly fineDots?: { readonly from: number; readonly to: number } | null;
  /** Objects cast shadows; off for comparison with `&shadows=0`. */
  readonly shadows?: boolean;
  /** The closest zoom and the lowest tilt, to try others; MAX_SCALE and MIN_PITCH when absent. */
  readonly maxScale?: number;
  readonly minPitch?: number;
  /**
   * Decision 0010: the digits shrink away as the tiles grow too small on the screen to
   * tap, by a tile's height there in device pixels: whole from `full`, gone from `gone` down.
   * Null or absent draws them whole.
   */
  readonly numberFade?: { readonly full: number; readonly gone: number } | null;
  /**
   * Decision 0010: numbers and banners are hidden where the world stands in front of them, and a
   * banner's foot stands at its tile's centre. On unless false.
   */
  readonly markersBehind?: boolean;
  readonly atlas: { readonly def: AtlasDef; readonly image: HTMLImageElement };
  readonly models: ModelSet;
  /** Dresses tiles instead of the placement rules: the model gallery. */
  readonly dress?: Dress;
  /**
   * The animals: birds, gnats, fish and deer, on unless false. Birds, gnats and fish are only
   * motion and are always off under reduced motion; deer stand still then.
   */
  readonly life?: boolean;
  /** The creature in every chasm instead of the placement rule's: a creature's portrait. */
  readonly creature?: string;
  /** The frame round the chasm that ended the run, on unless false: a creature's portrait leaves it out. */
  readonly killerRim?: boolean;
  /** Smoke from every chimney, fire and kiln, on unless false: the clearing's picture of a house leaves it out. */
  readonly smoke?: boolean;
  /**
   * Grass on the land tops (visual spec, Scene composition, Grass), on unless false: off for
   * comparison with `&grass=0`, and in the tray's pictures.
   */
  readonly grass?: boolean;
  /**
   * The flag planted on a bannered tile, a flag model of the set such as `iso-banner-b2-flag`;
   * the game's own, `iso-banner-1-flag`, when absent or unknown. A trial for now (decision 0033).
   */
  readonly banner?: string;
}

/** One quad of the marker pass: an atlas sprite by name, or a shape drawn by its mode. */
interface Quad {
  readonly name: string;
  readonly x: number;
  readonly y: number;
  readonly mode: number;
  /** A shape in a square box, this many art units from its centre to its edge. */
  readonly r?: number;
  /** The box's height over its width, below 1 for a shape lying flat on the ground. */
  readonly flat?: number;
  /** How much of a bird shows, from 0 to 1, through a fine stipple; all of it when absent. */
  readonly fade?: number;
  /** An atlas sprite's size, times its size in the atlas; 1 when absent. */
  readonly size?: number;
  /**
   * The window depth at the anchor, and its fall per device pixel above the anchor for
   * a marker standing up; never hidden by the world when absent.
   */
  readonly depth?: number;
  readonly rise?: number;
}

function hexToVec(hex: string): Vector3 {
  const v = Number.parseInt(hex.slice(1), 16);
  return new Vector3(((v >> 16) & 255) / 255, ((v >> 8) & 255) / 255, (v & 255) / 255);
}

function dataTexture(data: Uint8Array, w: number, h: number, linear: boolean): DataTexture {
  const tex = new DataTexture(data, w, h, RedFormat, UnsignedByteType);
  tex.minFilter = linear ? LinearFilter : NearestFilter;
  tex.magFilter = linear ? LinearFilter : NearestFilter;
  tex.unpackAlignment = 1;
  tex.colorSpace = NoColorSpace;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

interface ModelBatch {
  readonly mesh: InstancedMesh;
  readonly tile: InstancedBufferAttribute;
  readonly id: InstancedBufferAttribute;
  readonly flags: InstancedBufferAttribute;
  readonly sway: InstancedBufferAttribute;
  /** When a banner was placed and taken away, in the clock's seconds; read by banners only. */
  readonly life: InstancedBufferAttribute;
  capacity: number;
}

export class WorldRenderer {
  readonly fog = new FogState();
  theme: ThemeDef;
  dot: number;
  lock: DitherLock;
  reducedMotion: boolean;

  /** The point on the plateau the camera looks at, in tiles. */
  readonly target = new Vector2(0, 0);
  yaw = Math.PI / 4;
  pitch = Math.PI / 6;
  private zoom: number;

  private readonly gl: WebGLRenderer;
  private readonly camera = new OrthographicCamera();
  private readonly gbuffer: WebGLRenderTarget;
  private readonly worldScene = new ThreeScene();
  private readonly postScene = new ThreeScene();
  private readonly markerScene = new ThreeScene();
  private readonly screenCamera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly builder: SceneBuilder;
  private readonly models: ModelSet;
  /** How high a ray can meet anything: the tallest model at the largest size the rules give. */
  private readonly tallest: number;
  private readonly atlas: AtlasDef;

  private size = MIN_WINDOW;
  private weights = new Uint8Array(MIN_WINDOW * MIN_WINDOW);
  private near = new Uint8Array(MIN_WINDOW * MIN_WINDOW);
  private weightTex: DataTexture;
  private nearTex: DataTexture;
  /** Each tile's ground, from the scene (Scene.ground), for the grass on the tops. */
  private groundTex = dataTexture(
    new Uint8Array(MIN_WINDOW * MIN_WINDOW),
    MIN_WINDOW,
    MIN_WINDOW,
    false,
  );
  private readonly groundOrigin = { value: new Vector2() };
  /** Grass on the land tops: 1 on, 0 off. */
  private readonly grass = { value: 1 };
  private mistDirty = true;
  private mistWasAnimating = false;
  private readonly columnMaterial: RawShaderMaterial;
  private readonly modelMaterials = new Map<Bands, RawShaderMaterial>();
  private readonly postMaterial: RawShaderMaterial;
  private readonly postMesh: Mesh<PlaneGeometry, RawShaderMaterial>;
  /**
   * Tilt-shift, a trial: how strong, from 0 (off, the post pass alone, as before) to 1. When on,
   * the post pass writes its tone to tiltTarget and the tilt pass blurs it before the halftone.
   */
  private tilt = 0;
  private tiltTarget: WebGLRenderTarget | null = null;
  private tiltPrepass: RawShaderMaterial | null = null;
  private tiltMaterial: RawShaderMaterial | null = null;
  private readonly postUniforms: ReturnType<typeof makePostUniforms>;
  private readonly markerMaterial: RawShaderMaterial;
  private readonly markerUniforms: ReturnType<typeof makeMarkerUniforms>;
  private readonly markerGeometry: InstancedBufferGeometry;
  private markerBox = new InstancedBufferAttribute(new Float32Array(4 * 256), 4);
  private markerUv = new InstancedBufferAttribute(new Float32Array(4 * 256), 4);
  private markerMode = new InstancedBufferAttribute(new Float32Array(256), 1);
  private markerFade = new InstancedBufferAttribute(new Float32Array(256), 1);
  private markerDepth = new InstancedBufferAttribute(new Float32Array(3 * 256), 3);
  private columns: Mesh | null = null;
  private readonly overlay: Mesh;
  private overlayKey = "";
  private readonly batches = new Map<string, ModelBatch>();
  private scene: Scene | null = null;
  private window = { x: 0, y: 0 };
  private dirty = true;
  private devW = 1;
  private devH = 1;
  private glide = new Vector2(0, 0);
  private lastPan = 0;
  private lastFrame = 0;
  private gliding = false;
  /** Whether birds or their shadows showed in the last frame. */
  private flying = false;
  /** Chimney tops by model name, in the model's own space: smoke rises from every one. */
  private readonly chimneys = new Map<string, Vector3>();
  private readonly shadowTarget: WebGLRenderTarget;
  private readonly shadowCamera = new OrthographicCamera();
  private readonly shadowColumn: RawShaderMaterial;
  private readonly shadowModel: RawShaderMaterial;
  private readonly shadowsOn: boolean;
  /** Zoomed out, a finer halftone; null keeps the 8 by 8 screen throughout. Can change live. */
  fineDots: { readonly from: number; readonly to: number } | null;
  /** Shared by the column and model materials, so one update reaches them all. */
  private readonly shadowUniforms = {
    uShadowMap: { value: null as DepthTexture | null },
    uShadowMatrix: { value: new Matrix4() },
    uShadowDir: { value: new Vector3(...shadowSunDirection()) },
    uShadowTexel: { value: 1 / SHADOW_SIZE },
    uShadowOn: { value: 1 },
  };
  /** Seconds, shared by the model materials for the wind. */
  private readonly windTime = { value: 0 };
  /** The tone of the houses' interiors, from the theme. */
  private readonly glow = { value: GLOW_TONE };
  /** The camera's turn, shared by the model materials so a banner can face it. */
  private readonly camYaw = { value: Math.PI / 4 };
  /** The height the banner's cloth hangs from, from the model's cloth probe. */
  private readonly bannerTop = { value: BANNER_TOP };
  /**
   * Which way the banner's cloth flies, for its wind: none for a cloth that hangs, whose ripple
   * runs down from its top; for a cloth fixed along its staff, the way from its cloth probe to
   * its fly probe, and in w how far along that way its cloth probe lies (decision 0035).
   */
  private readonly bannerFly = { value: new Vector4() };
  /** The height the home flagpole's pennant hangs from, from its cloth probe. */
  private readonly pennantTop = { value: 1.2 };
  /**
   * Banners by tile, to animate them: where each stands, when it was placed and, once taken
   * away, when, in the clock's seconds (gone below 0 while it stands). Banners there when a run
   * is first drawn count as placed long ago, so they stand already unrolled.
   */
  private readonly banners = new Map<
    string,
    { x: number; y: number; tx: number; ty: number; born: number; gone: number }
  >();
  private bannersSeen = false;
  private readonly risen = new Map<string, number>();
  /** When the creatures began to sink back into their gaps, in the clock's seconds; below 0 while they stand. */
  private creaturesGone = -1;
  /** Whether the run has been drawn yet: creatures already risen when it is first drawn stand. */
  private drawnOnce = false;
  private readonly creatureModels: ReadonlySet<string>;
  private readonly killerRim: boolean;
  /** A thing alone, with no ground and no mist: `setBare`. */
  private bare = false;
  private life: boolean;
  private readonly lifeWanted: boolean;
  private readonly maxScale: number;
  private readonly minPitch: number;
  private readonly numberFade: { readonly full: number; readonly gone: number } | null;
  private readonly markersBehind: boolean;
  /** Where each flock in the air set out from, by its slot: the camera's target then. */
  private readonly flights = new Map<number, Vector2>();
  /** The flocks the last frame drew on the screen, for the app to hear their wings. */
  private flocksDrawn: Flock[] = [];
  private readonly gnats: Mesh<InstancedBufferGeometry, RawShaderMaterial>;
  private gnatPos = new InstancedBufferAttribute(new Float32Array(3 * 256), 3);
  private readonly gnatUniforms = { uViewport: { value: new Vector2(1, 1) }, uSize: { value: 2 } };
  private readonly fish: Mesh<InstancedBufferGeometry, RawShaderMaterial>;
  private fishAt = new InstancedBufferAttribute(new Float32Array(4 * 32), 4);
  private readonly fishUniforms = {
    uViewport: { value: new Vector2(1, 1) },
    uLength: { value: 8 },
  };
  /** Rings on the water where fish leapt, read by the column shader. */
  private readonly splashUniforms = {
    uSplashes: { value: Array.from({ length: MAX_SPLASHES }, () => new Vector4()) },
    uSplashCount: { value: 0 },
  };

  readonly canvas: HTMLCanvasElement;

  constructor(canvas: HTMLCanvasElement, options: RendererOptions) {
    this.canvas = canvas;
    this.theme = options.theme;
    this.dot = options.dot;
    this.lock = options.lock;
    this.reducedMotion = options.reducedMotion;
    this.lifeWanted = options.life !== false;
    this.life = this.lifeWanted && !options.reducedMotion;
    this.maxScale = options.maxScale ?? MAX_SCALE;
    this.numberFade = options.numberFade ?? null;
    this.markersBehind = options.markersBehind !== false;
    this.minPitch = options.minPitch ?? MIN_PITCH;
    this.zoom = clamp(options.scale, MIN_SCALE, this.maxScale);
    this.models = options.models;
    this.atlas = options.atlas.def;
    for (const [key, point] of options.models.probes) {
      const [model, probe] = key.split("/");
      if (model && probe === "chimney-top") this.chimneys.set(model, point.clone());
    }
    // Riders and motions, as the models name them (decision 0019). A rider sits at the probe it
    // names on the model its name extends, moving by its own motion: a mill's sails spin at its
    // hub, a water mill's wheel at its axle, a deer's head nods at its neck, the home cabin's
    // flagpole stands at its spot with its pennant. Any other model that names a motion moves
    // by it wherever it is placed: the eagle soars, the duck dabbles.
    const attachments = new Map<string, Attachment[]>();
    const motions = new Map<string, number>();
    for (const [name, model] of options.models.models) {
      const flags = model.motion ? (MOTION_FLAGS[model.motion] ?? 0) : 0;
      if (!model.rides) {
        if (flags) motions.set(name, flags);
        continue;
      }
      const host = name.slice(0, name.lastIndexOf("-"));
      const point = options.models.probes.get(`${host}/${model.rides}`);
      if (!point) continue;
      attachments.set(host, [
        ...(attachments.get(host) ?? []),
        { name, x: point.x, y: point.y, z: point.z, flags },
      ]);
    }
    // Without life the placement rules find no animal's model, so none come: the catalog's
    // animals, with their riders.
    const animals = CATALOG.families.filter((f) => f.animal).map((f) => `iso-${f.name}-`);
    const names = [...options.models.models.keys()].filter(
      (name) => options.life !== false || !animals.some((a) => name.startsWith(a)),
    );
    for (const [key, point] of options.models.probes) {
      if (key.endsWith("-flagpole/cloth")) this.pennantTop.value = point.y;
    }
    // Decision 0012: after death a creature rises out of every chasm within reach. The ones that
    // did not end the run sink until their eyes come to just above the rim. Where a creature
    // stands its gap keeps its mist.
    const only = options.creature;
    const at = (world: WorldSource, x: number, y: number) =>
      only ?? creatureOn(world.terrain(x, y) as Terrain, x, y);
    const peeks = new Map<string, number>();
    for (const model of [...Object.values(CREATURES).flat(), ...(only ? [only] : [])]) {
      const eyes = options.models.probes.get(`${model}/eyes`);
      if (eyes) peeks.set(model, eyes.y - 0.06);
    }
    // Smoke, models in the world (Daniel, 2026-10-20): the puffs rise from every chimney top, at
    // the size the catalog gives the model's family: smaller from a campfire or a charcoal kiln.
    const smokeSources = new Map<string, SmokeSource[]>();
    if (options.smoke !== false && options.models.models.has(PUFF)) {
      for (const [model, point] of this.chimneys) {
        const size = familyOf(model)?.smoke ?? 1;
        smokeSources.set(model, [{ x: point.x, y: point.y, z: point.z, size }]);
      }
    }
    this.builder = new SceneBuilder(
      names,
      options.dress ?? null,
      attachments,
      { at, peeks },
      smokeSources,
      motions,
    );
    this.creatureModels = new Set(peeks.keys());
    this.tallest = 1.3 * Math.max(...[...this.models.models.values()].map((m) => m.box.max[1]));
    this.plantBanner(options.banner ?? null);
    this.killerRim = options.killerRim !== false;
    const known = new Set(names);
    this.fog.keepMist = (world, x, y) =>
      world.status === "dead" &&
      world.isChasm(x, y) &&
      !world.hasBanner(x, y) &&
      known.has(at(world, x, y));

    this.gl = new WebGLRenderer({
      canvas,
      antialias: false,
      alpha: false,
      depth: true,
      preserveDrawingBuffer: options.preserveDrawingBuffer,
      powerPreference: "high-performance",
    });
    this.gl.setPixelRatio(1);
    this.gl.autoClear = false;

    this.gbuffer = new WebGLRenderTarget(1, 1, {
      count: 2,
      type: UnsignedByteType,
      minFilter: NearestFilter,
      magFilter: NearestFilter,
      depthBuffer: true,
      generateMipmaps: false,
      // Read by the marker pass, so numbers and banners can go behind the world (decision 0010).
      depthTexture: new DepthTexture(1, 1),
    });

    this.weightTex = dataTexture(this.weights, MIN_WINDOW, MIN_WINDOW, false);
    this.nearTex = dataTexture(this.near, MIN_WINDOW, MIN_WINDOW, false);
    const noise = dataTexture(makeNoiseTexture(), NOISE_SIZE, NOISE_SIZE, true);
    noise.wrapS = RepeatWrapping;
    noise.wrapT = RepeatWrapping;

    const sun = new Vector3(...sunDirection());
    this.columnMaterial = new RawShaderMaterial({
      glslVersion: GLSL3,
      vertexShader: COLUMN_VERTEX,
      fragmentShader: COLUMN_FRAGMENT,
      uniforms: {
        uWeights: { value: this.weightTex },
        uDepth: { value: DEPTH },
        uTime: this.windTime,
        uGround: { value: this.groundTex },
        uGroundOrigin: this.groundOrigin,
        uGrass: this.grass,
        ...this.shadowUniforms,
        ...this.splashUniforms,
      },
      side: DoubleSide,
    });
    for (const [bands, value] of [
      ["plain", 0],
      ["tree", 1],
      ["crop", 2],
    ] as const) {
      this.modelMaterials.set(
        bands,
        new RawShaderMaterial({
          glslVersion: GLSL3,
          vertexShader: MODEL_VERTEX,
          fragmentShader: MODEL_FRAGMENT,
          uniforms: {
            uWeights: { value: this.weightTex },
            uDepth: { value: DEPTH },
            uSun: { value: sun },
            uBands: { value },
            uGlow: this.glow,
            uTime: this.windTime,
            uCamYaw: this.camYaw,
            uBannerTop: this.bannerTop,
            uBannerFly: this.bannerFly,
            uPennantTop: this.pennantTop,
            ...this.shadowUniforms,
          },
          side: DoubleSide,
        }),
      );
    }

    // The shadow pass: the same geometry, seen from the shadow sun, depth only.
    this.shadowsOn = options.shadows !== false;
    this.grass.value = options.grass === false ? 0 : 1;
    this.fineDots = options.fineDots ?? null;
    this.shadowUniforms.uShadowOn.value = this.shadowsOn ? 1 : 0;
    const depthTexture = new DepthTexture(SHADOW_SIZE, SHADOW_SIZE);
    this.shadowTarget = new WebGLRenderTarget(SHADOW_SIZE, SHADOW_SIZE, {
      depthTexture,
      depthBuffer: true,
      generateMipmaps: false,
    });
    this.shadowUniforms.uShadowMap.value = depthTexture;
    this.shadowColumn = new RawShaderMaterial({
      glslVersion: GLSL3,
      vertexShader: COLUMN_VERTEX,
      fragmentShader: SHADOW_FRAGMENT,
      defines: { SHADOW_PASS: 1 },
      uniforms: { uWeights: { value: this.weightTex }, uDepth: { value: DEPTH } },
      side: DoubleSide,
    });
    this.shadowModel = new RawShaderMaterial({
      glslVersion: GLSL3,
      vertexShader: MODEL_VERTEX,
      fragmentShader: SHADOW_FRAGMENT,
      defines: { SHADOW_PASS: 1 },
      uniforms: {
        uWeights: { value: this.weightTex },
        uDepth: { value: DEPTH },
        uTime: this.windTime,
        uCamYaw: this.camYaw,
        uBannerTop: this.bannerTop,
        uBannerFly: this.bannerFly,
        uPennantTop: this.pennantTop,
      },
      side: DoubleSide,
    });

    this.postUniforms = makePostUniforms(
      this.gbuffer,
      this.weightTex,
      this.nearTex,
      noise,
      options,
    );
    this.postMaterial = new RawShaderMaterial({
      glslVersion: GLSL3,
      vertexShader: POST_VERTEX,
      fragmentShader: POST_FRAGMENT,
      depthTest: false,
      depthWrite: false,
      uniforms: this.postUniforms,
    });
    const post = new Mesh(new PlaneGeometry(2, 2), this.postMaterial);
    post.frustumCulled = false;
    this.postScene.add(post);
    this.postMesh = post;

    // Sampled bilinearly: the digits are distance fields, so their alpha-tested edge stays smooth
    // however close the camera comes; the other markers' coverage keeps its shape.
    const atlasTex = new Texture(options.atlas.image);
    atlasTex.flipY = false;
    atlasTex.minFilter = LinearFilter;
    atlasTex.magFilter = LinearFilter;
    atlasTex.generateMipmaps = false;
    atlasTex.colorSpace = NoColorSpace;
    atlasTex.needsUpdate = true;
    this.markerUniforms = makeMarkerUniforms(atlasTex, this.gbuffer.depthTexture);
    this.markerMaterial = new RawShaderMaterial({
      glslVersion: GLSL3,
      vertexShader: MARKER_VERTEX,
      fragmentShader: MARKER_FRAGMENT,
      side: DoubleSide,
      depthTest: false,
      depthWrite: false,
      uniforms: this.markerUniforms,
    });
    this.markerGeometry = new InstancedBufferGeometry();
    this.markerGeometry.setAttribute(
      "position",
      new Float32BufferAttribute([0, 0, 0, 1, 0, 0, 1, 1, 0, 0, 0, 0, 1, 1, 0, 0, 1, 0], 3),
    );
    this.setMarkerCapacity(256);
    const markers = new Mesh(this.markerGeometry, this.markerMaterial);
    markers.frustumCulled = false;
    this.markerScene.add(markers);

    this.overlay = new Mesh(
      new BufferGeometry(),
      new RawShaderMaterial({
        glslVersion: GLSL3,
        vertexShader: OVERLAY_VERTEX,
        fragmentShader: OVERLAY_FRAGMENT,
        side: DoubleSide,
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -4,
      }),
    );
    this.overlay.frustumCulled = false;
    this.worldScene.add(this.overlay);

    const gnatGeometry = new InstancedBufferGeometry();
    gnatGeometry.setAttribute(
      "position",
      new Float32BufferAttribute([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, -1, 0, 1, 1, 0, -1, 1, 0], 3),
    );
    gnatGeometry.setAttribute("iPos", this.gnatPos);
    gnatGeometry.instanceCount = 0;
    this.gnats = new Mesh(
      gnatGeometry,
      new RawShaderMaterial({
        glslVersion: GLSL3,
        vertexShader: GNAT_VERTEX,
        fragmentShader: GNAT_FRAGMENT,
        side: DoubleSide,
        uniforms: this.gnatUniforms,
      }),
    );
    this.gnats.frustumCulled = false;
    this.worldScene.add(this.gnats);

    const fishGeometry = new InstancedBufferGeometry();
    fishGeometry.setAttribute("position", gnatGeometry.getAttribute("position"));
    fishGeometry.setAttribute("iFish", this.fishAt);
    fishGeometry.instanceCount = 0;
    this.fish = new Mesh(
      fishGeometry,
      new RawShaderMaterial({
        glslVersion: GLSL3,
        vertexShader: FISH_VERTEX,
        fragmentShader: FISH_FRAGMENT,
        side: DoubleSide,
        uniforms: this.fishUniforms,
      }),
    );
    this.fish.frustumCulled = false;
    this.worldScene.add(this.fish);

    // Three re-creates its GPU objects when the context comes back; the scene and the mist
    // textures must then be uploaded again.
    canvas.addEventListener("webglcontextrestored", () => {
      this.dirty = true;
      this.mistDirty = true;
      this.weightTex.needsUpdate = true;
      this.nearTex.needsUpdate = true;
      this.overlayKey = "";
    });
  }

  // The view.

  get viewport(): { w: number; h: number } {
    return { w: this.devW, h: this.devH };
  }

  get scale(): number {
    return this.zoom;
  }

  /**
   * The scene the last frame drew, for what the app reads of it: the ground that shows, what
   * stands there and what lives there. Null before the first frame.
   */
  get lastScene(): Scene | null {
    return this.scene;
  }

  /** Whether the world's life is drawn: on unless `&life=0`, and resting under reduced motion. */
  get living(): boolean {
    return this.life;
  }

  /** The flocks the last frame drew on the screen; none without life. */
  get flocks(): readonly Flock[] {
    return this.flocksDrawn;
  }

  /**
   * A tile's height on the screen in device pixels, as a diamond seen from 45 degrees around, so
   * it does not change as the world turns: 48 s sin(pitch).
   */
  private tileHeight(): number {
    return ART_PER_TILE * Math.SQRT2 * this.zoom * Math.sin(this.pitch);
  }

  /** How large the digits are drawn, 0 to 1: they shrink away as tiles grow too small to tap. */
  numberScale(): number {
    const fade = this.numberFade;
    return fade ? smoothstep(fade.gone, fade.full, this.tileHeight()) : 1;
  }

  /** The scale at which the digits are whole at the present tilt, never below the present one. */
  readableScale(): number {
    const fade = this.numberFade;
    if (!fade) return this.zoom;
    const scale = (1.1 * fade.full) / (ART_PER_TILE * Math.SQRT2 * Math.sin(this.pitch));
    return clamp(scale, this.zoom, this.maxScale);
  }

  resize(devW: number, devH: number): void {
    this.devW = Math.max(1, Math.round(devW));
    this.devH = Math.max(1, Math.round(devH));
    this.gl.setSize(this.devW, this.devH, false);
    this.gbuffer.setSize(this.devW, this.devH);
    this.tiltTarget?.setSize(this.devW, this.devH);
    this.updateCamera();
  }

  invalidate(): void {
    this.dirty = true;
    this.mistDirty = true;
  }

  focus(): { x: number; y: number } {
    return { x: this.target.x, y: this.target.y };
  }

  setFocus(p: { x: number; y: number }): void {
    this.target.set(p.x, p.y);
    this.glide.set(0, 0);
    this.updateCamera();
  }

  centreOnTile(x: number, y: number): void {
    this.setFocus({ x, y });
  }

  /** Where the camera looks now, to come back to. */
  cameraView(): CameraView {
    return {
      x: this.target.x,
      y: this.target.y,
      yaw: this.yaw,
      pitch: this.pitch,
      scale: this.zoom,
    };
  }

  /** Looks as a view did, its tilt and scale kept within this camera's limits. A glide stops. */
  setCameraView(view: CameraView): void {
    this.target.set(view.x, view.y);
    this.yaw = view.yaw;
    this.pitch = clamp(view.pitch, this.minPitch, MAX_PITCH);
    this.zoom = clamp(view.scale, MIN_SCALE, this.maxScale);
    this.glide.set(0, 0);
    this.gliding = false;
    this.updateCamera();
  }

  tileAt(devX: number, devY: number): Coord {
    const p = this.groundAt(devX, devY, 0);
    return { x: Math.round(p.x), y: Math.round(p.y) };
  }

  /** The point of the ground under a device pixel, in tiles: x and y along the plateau's top. */
  pointAt(devX: number, devY: number): { x: number; y: number } {
    const p = this.groundAt(devX, devY, 0);
    return { x: p.x, y: p.y };
  }

  /**
   * What a tap at a device pixel touched, where it changed nothing in the run: what the view ray
   * meets first, else what stands on the tile under it (decision 0023; visual spec, What a tap
   * says). `now` is the clock the mist runs on.
   */
  thingAt(world: WorldSource, devX: number, devY: number, now: number): Thing | null {
    const p = this.groundAt(devX, devY, 0);
    const ray = { px: p.x, py: p.y, yaw: this.yaw, pitch: this.pitch };
    return thingAt(this.touchSource(world, now), ray, this.tallest);
  }

  /** What stands on a tile, for a press without a finger: the keyboard's. */
  thingOn(world: WorldSource, x: number, y: number, now: number): Thing | null {
    return thingOn(this.touchSource(world, now), x, y);
  }

  private touchSource(world: WorldSource, now: number): TouchSource {
    return {
      world,
      pick: this.builder.modelPick,
      box: (model) => this.models.models.get(model)?.box ?? null,
      clear: (x, y) => this.fog.weight(world, x, y, now, this.reducedMotion) <= 0.02,
      home: this.builder.homeModel,
    };
  }

  tileToDevice(x: number, y: number): { x: number; y: number } {
    return this.project(new Vector3(x, 0, y));
  }

  /** A point of the world in device pixels: x and y along the ground, h above it, in tiles. */
  pointToDevice(x: number, y: number, h: number): { x: number; y: number } {
    return this.project(new Vector3(x, h, y));
  }

  panBy(devDx: number, devDy: number): void {
    const a = this.groundAt(this.devW / 2, this.devH / 2, 0);
    const b = this.groundAt(this.devW / 2 - devDx, this.devH / 2 - devDy, 0);
    const now = performance.now();
    const dt = Math.max(8, now - this.lastPan);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    // Velocity in tiles per millisecond, smoothed, for the glide when the finger lifts.
    this.glide.set(this.glide.x * 0.5 + (dx / dt) * 0.5, this.glide.y * 0.5 + (dy / dt) * 0.5);
    this.lastPan = now;
    this.target.x += dx;
    this.target.y += dy;
    this.gliding = false;
    this.updateCamera();
  }

  settle(): void {
    if (this.reducedMotion || performance.now() - this.lastPan > 80) {
      this.glide.set(0, 0);
      return;
    }
    this.gliding = this.glide.length() > 0.0005;
  }

  /** Whether the camera glides on after a pan. */
  get isGliding(): boolean {
    return this.gliding;
  }

  /** The glide stops where the camera is: a finger or a click came down on the world. */
  stopGlide(): void {
    this.glide.set(0, 0);
    this.gliding = false;
  }

  zoomAround(scale: number, devX: number, devY: number): void {
    this.keepPut(devX, devY, () => {
      this.zoom = clamp(scale, MIN_SCALE, this.maxScale);
    });
  }

  rotateBy(radians: number, devX: number, devY: number): void {
    this.keepPut(devX, devY, () => {
      this.yaw += radians;
    });
  }

  tiltBy(radians: number): void {
    this.pitch = clamp(this.pitch + radians, this.minPitch, MAX_PITCH);
    this.updateCamera();
  }

  // Camera.

  /** Runs a change to the camera so that the ground under a device pixel stays under it. */
  private keepPut(devX: number, devY: number, change: () => void): void {
    const before = this.groundAt(devX, devY, 0);
    change();
    this.updateCamera();
    const after = this.groundAt(devX, devY, 0);
    this.target.x += before.x - after.x;
    this.target.y += before.y - after.y;
    this.updateCamera();
  }

  private updateCamera(): void {
    const k = this.zoom * ART_PER_TILE;
    const hw = this.devW / (2 * k);
    const hh = this.devH / (2 * k);
    const cam = this.camera;
    cam.left = -hw;
    cam.right = hw;
    cam.top = hh;
    cam.bottom = -hh;
    cam.near = 0.1;
    cam.far = 400;
    const dist = 150;
    const cp = Math.cos(this.pitch);
    cam.position.set(
      this.target.x + dist * cp * Math.sin(this.yaw),
      dist * Math.sin(this.pitch),
      this.target.y + dist * cp * Math.cos(this.yaw),
    );
    cam.up.set(0, 1, 0);
    cam.lookAt(this.target.x, 0, this.target.y);
    cam.updateProjectionMatrix();
    cam.updateMatrixWorld(true);
  }

  /** The point at height `h` under a device pixel, in tiles. */
  private groundAt(devX: number, devY: number, h: number): Vector2 {
    const ndc = new Vector3((devX / this.devW) * 2 - 1, 1 - (devY / this.devH) * 2, -1);
    const o = ndc.unproject(this.camera);
    const dir = this.camera.getWorldDirection(new Vector3());
    const t = (h - o.y) / dir.y;
    return new Vector2(o.x + t * dir.x, o.z + t * dir.z);
  }

  private project(p: Vector3): { x: number; y: number } {
    const v = p.clone().project(this.camera);
    return { x: ((v.x + 1) / 2) * this.devW, y: ((1 - v.y) / 2) * this.devH };
  }

  // Scene.

  /**
   * The window size the camera needs: the farthest the ground it sees lies from the target, on
   * the plateau and on the mist floor, with a margin for tall objects, in steps of 32 tiles.
   */
  private neededWindow(): number {
    let reach = 0;
    for (const [x, y] of [
      [0, 0],
      [this.devW, 0],
      [0, this.devH],
      [this.devW, this.devH],
    ] as const) {
      for (const h of [0, -DEPTH]) {
        const p = this.groundAt(x, y, h);
        reach = Math.max(reach, Math.abs(p.x - this.target.x), Math.abs(p.y - this.target.y));
      }
    }
    const size = Math.ceil((2 * reach + 12) / 32) * 32;
    return clamp(Number.isFinite(size) ? size : MAX_WINDOW, MIN_WINDOW, MAX_WINDOW);
  }

  /** Makes the mist textures `size` tiles square, when they are not already. */
  private resizeWindow(size: number): void {
    if (size === this.size) return;
    this.size = size;
    this.weights = new Uint8Array(size * size);
    this.near = new Uint8Array(size * size);
    this.weightTex.dispose();
    this.nearTex.dispose();
    this.weightTex = dataTexture(this.weights, size, size, false);
    this.nearTex = dataTexture(this.near, size, size, false);
    for (const m of [
      this.columnMaterial,
      ...this.modelMaterials.values(),
      this.shadowColumn,
      this.shadowModel,
    ]) {
      const u = m.uniforms.uWeights;
      if (u) u.value = this.weightTex;
    }
    this.postUniforms.uWeights.value = this.weightTex;
    this.postUniforms.uNear.value = this.nearTex;
    this.postUniforms.uWinSize.value = size;
  }

  /** Reduced motion from the player's setting: animals rest, fades and rises are instant. */
  setReducedMotion(on: boolean): void {
    this.reducedMotion = on;
    this.life = this.lifeWanted && !on;
    if (on) this.settleLives();
    // The mist's clouds drift again, or rest, as the setting says: before, only the setting the
    // renderer was made with counted, so turning reduced motion off left the mist still.
    this.postUniforms.uDrift.value = on ? 0 : 1;
    this.invalidate();
  }

  /**
   * Under reduced motion the clock reads 0, so what was placed or rose on the clock would read as
   * placed in the future and sink out of sight: the banners standing and the creatures risen stand
   * from long ago instead, as if the run had been drawn under reduced motion, and the banners
   * still leaving are gone.
   */
  private settleLives(): void {
    for (const [key, b] of this.banners) {
      if (b.gone >= 0) this.banners.delete(key);
      else b.born = -1e6;
    }
    for (const key of this.risen.keys()) this.risen.set(key, -1e6);
    this.dirty = true;
  }

  /**
   * Dresses tiles from now on instead of the placement rules, or by the rules again with null:
   * the game turns from a run to the clearing and back in place. With `life` the dressed world
   * keeps the world's own gnats and fish, as the clearing does (Daniel, 2026-10-19).
   */
  setDress(dress: Dress | null, life = false): void {
    this.builder.setDress(dress, life);
    this.invalidate();
  }

  /**
   * The player's own cabin from now on (decision 0029), or the first cabin again with null: a
   * house in the cabin's place, with the cabin's flagpole beside it, on this screen alone.
   */
  setHome(home: string | null): void {
    this.builder.setHome(home);
    this.invalidate();
  }

  /**
   * The flag planted on every bannered tile from now on, a flag model of the set, or the game's
   * own again with null: the banner a supporter chose (decision 0035). Banners already standing
   * stay standing.
   */
  setBanner(flag: string | null): void {
    this.plantBanner(flag);
    this.invalidate();
  }

  /**
   * Chooses the flag, the height its cloth unrolls from, from the flag's cloth probe, and the way
   * its cloth flies, from its fly probe where it has one.
   */
  private plantBanner(flag: string | null): void {
    this.builder.setBanner(flag);
    const name = this.builder.bannerFlag;
    const cloth = this.models.probes.get(`${name}/cloth`);
    this.bannerTop.value = cloth?.y ?? BANNER_TOP;
    const fly = this.models.probes.get(`${name}/fly`);
    if (cloth && fly) {
      const way = fly.clone().sub(cloth).normalize();
      this.bannerFly.value.set(way.x, way.y, way.z, way.dot(cloth));
    } else {
      this.bannerFly.value.set(0, 0, 0, 0);
    }
  }

  /**
   * A thing alone, for its picture in the clearing's tray (Daniel, 2026-10-20): no ground under
   * it and no mist round it, so it stands on paper and can fill the picture however small it is.
   */
  setBare(bare: boolean): void {
    this.bare = bare;
    if (this.columns) this.columns.visible = !bare;
    this.invalidate();
  }

  /** A new run: its banners, when first drawn, stand already unrolled. */
  startRun(): void {
    this.banners.clear();
    this.risen.clear();
    this.creaturesGone = -1;
    this.drawnOnce = false;
    this.bannersSeen = false;
  }

  /**
   * The creatures sink back into their gaps from `now`, as a banner taken away does: the journey,
   * a trial of apps/web, closes the mist over the run before it opens it again.
   */
  lowerCreatures(now: number): void {
    this.creaturesGone = now / 1000;
    this.invalidate();
  }

  /** Forgets the creatures risen, so each rises out of its gap again when it is next drawn. */
  forgetCreatures(): void {
    this.risen.clear();
    this.creaturesGone = -1;
    this.invalidate();
  }

  /**
   * Notes which banners were placed or taken away since the last scene, and returns those taken
   * away that are still rolling up and sinking, to draw beside the scene's own.
   */
  private bannerChanges(scene: Scene, now: number): Instance[] {
    const t = now / 1000;
    const instant = this.reducedMotion || !this.bannersSeen;
    const standing = new Set<string>();
    for (const b of scene.instances.get(this.builder.bannerFlag) ?? []) {
      const key = `${b.tx},${b.ty}`;
      standing.add(key);
      const known = this.banners.get(key);
      if (!known || known.gone >= 0) {
        this.banners.set(key, {
          x: b.x,
          y: b.y,
          tx: b.tx,
          ty: b.ty,
          born: instant ? -1e6 : t,
          gone: -1,
        });
      }
    }
    const leaving: Instance[] = [];
    for (const [key, b] of this.banners) {
      if (standing.has(key)) continue;
      if (b.gone < 0) b.gone = instant ? -1e6 : t;
      if (t - b.gone > BANNER_LEAVE) {
        this.banners.delete(key);
        continue;
      }
      leaving.push({
        x: b.x,
        y: b.y,
        h: 0,
        tx: b.tx,
        ty: b.ty,
        flags: FLAG.aloft | FLAG.face,
        yaw: 0,
        scale: 1,
        sway: SWAY.none,
        slot: 30,
      });
    }
    this.bannersSeen = true;
    return leaving;
  }

  /** When a creature was first drawn, so it rises out of the mist once. */
  private risenAt(key: string, now: number): number {
    let born = this.risen.get(key);
    if (born === undefined) {
      born = this.reducedMotion || !this.drawnOnce ? -1e6 : now / 1000;
      this.risen.set(key, born);
    }
    return born;
  }

  private rebuild(world: WorldSource, size: number, now: number): void {
    this.resizeWindow(size);
    const cx = Math.round(this.target.x);
    const cy = Math.round(this.target.y);
    const scene = this.builder.build(world, cx, cy, size);
    this.scene = scene;
    this.window = { x: scene.x0, y: scene.y0 };
    this.groundTex.dispose();
    this.groundTex = dataTexture(scene.ground, scene.size, scene.size, false);
    const groundUniform = this.columnMaterial.uniforms.uGround;
    if (groundUniform) groundUniform.value = this.groundTex;
    this.groundOrigin.value.set(scene.x0, scene.y0);

    if (this.columns) {
      this.worldScene.remove(this.columns);
      this.columns.geometry.dispose();
    }
    const g = new BufferGeometry();
    g.setAttribute("position", new BufferAttribute(scene.columns.position, 3));
    g.setAttribute("aTone", new BufferAttribute(scene.columns.tone, 1));
    g.setAttribute("aId", new BufferAttribute(scene.columns.id, 1));
    g.setAttribute("aTile", new BufferAttribute(scene.columns.tile, 2));
    g.setAttribute("aKind", new BufferAttribute(scene.columns.kind, 1));
    this.columns = new Mesh(g, this.columnMaterial);
    this.columns.frustumCulled = false;
    this.columns.visible = !this.bare;
    this.worldScene.add(this.columns);

    const m = new Matrix4();
    const turn = new Matrix4();
    const grow = new Matrix4();
    for (const [name, batch] of this.batches) {
      if (!scene.instances.has(name)) batch.mesh.count = 0;
    }
    const leaving = this.bannerChanges(scene, now);
    const lists = new Map(scene.instances);
    const flag = this.builder.bannerFlag;
    if (leaving.length > 0) lists.set(flag, [...(lists.get(flag) ?? []), ...leaving]);
    for (const [name, list] of lists) {
      const batch = this.batch(name, list.length);
      if (!batch) continue;
      const banner = name === flag;
      // A field's tiles share its family's id, so a field of one crop is drawn as one.
      const family = familyOf(name);
      const joined = family?.joins ? joinedId(CATALOG.families.indexOf(family)) : null;
      const creature = this.creatureModels.has(name);
      list.forEach((inst, i) => {
        m.makeTranslation(inst.x, inst.h, inst.y)
          .multiply(turn.makeRotationY(inst.yaw))
          .multiply(grow.makeScale(inst.scale, inst.scale, inst.scale));
        batch.mesh.setMatrixAt(i, m);
        batch.tile.setXY(i, inst.tx - scene.x0, inst.ty - scene.y0);
        batch.id.setX(i, joined ?? instanceId(inst.tx, inst.ty, inst.slot));
        batch.flags.setX(i, inst.flags);
        batch.sway.setX(i, this.reducedMotion ? 0 : inst.sway);
        // A puff of smoke carries its number among its source's puffs and the source's size.
        const life = banner
          ? this.banners.get(`${inst.tx},${inst.ty}`)
          : creature
            ? { born: this.risenAt(`${inst.tx},${inst.ty}`, now), gone: this.creaturesGone }
            : inst.puff
              ? { born: inst.puff.k, gone: inst.puff.size }
              : undefined;
        batch.life.setXY(i, life?.born ?? -1e6, life?.gone ?? -1);
      });
      batch.mesh.count = list.length;
      batch.mesh.instanceMatrix.needsUpdate = true;
      batch.tile.needsUpdate = true;
      batch.id.needsUpdate = true;
      batch.flags.needsUpdate = true;
      batch.sway.needsUpdate = true;
      batch.life.needsUpdate = true;
    }
    this.dirty = false;
    this.mistDirty = true;
    this.drawnOnce = true;
  }

  private batch(name: string, needed: number): ModelBatch | null {
    let batch = this.batches.get(name);
    if (batch && batch.capacity >= needed) return batch;
    const model = this.models.models.get(name);
    if (!model) return null;
    const capacity = Math.max(16, needed * 2);
    if (batch) {
      this.worldScene.remove(batch.mesh);
      batch.mesh.dispose();
    }
    const geometry = model.geometry.clone();
    const tile = new InstancedBufferAttribute(new Float32Array(capacity * 2), 2);
    const id = new InstancedBufferAttribute(new Float32Array(capacity), 1);
    const flags = new InstancedBufferAttribute(new Float32Array(capacity), 1);
    const sway = new InstancedBufferAttribute(new Float32Array(capacity), 1);
    const life = new InstancedBufferAttribute(new Float32Array(capacity * 2), 2);
    geometry.setAttribute("iLife", life);
    geometry.setAttribute("iTile", tile);
    geometry.setAttribute("iId", id);
    geometry.setAttribute("iFlags", flags);
    geometry.setAttribute("iSway", sway);
    const material = this.modelMaterials.get(model.bands) as RawShaderMaterial;
    const mesh = new InstancedMesh(geometry, material, capacity);
    mesh.frustumCulled = false;
    this.worldScene.add(mesh);
    batch = { mesh, tile, id, flags, sway, life, capacity };
    this.batches.set(name, batch);
    return batch;
  }

  /** Refills the mist textures after a change and while a fade runs; at rest they stay. */
  private updateMist(world: WorldSource, now: number): void {
    this.fog.prune(now);
    // One more fill after the last fade has finished, so every tile settles at its end weight.
    const animating = this.fog.animating;
    if (!this.mistDirty && !animating && !this.mistWasAnimating) return;
    this.mistWasAnimating = animating;
    this.mistDirty = false;
    const size = this.size;
    this.fog.fill(
      this.weights,
      world,
      this.window.x,
      this.window.y,
      size,
      size,
      now,
      this.reducedMotion,
    );
    nearness(this.weights, size, size, this.near);
    this.weightTex.needsUpdate = true;
    this.nearTex.needsUpdate = true;
  }

  // Shadows.

  /**
   * Fits the shadow sun's camera around what the view sees, from the mist floor to the treetops,
   * and renders the scene's depth from it with the shadow materials.
   */
  private renderShadows(): void {
    const dir = this.shadowUniforms.uShadowDir.value;
    const cam = this.shadowCamera;
    cam.position.set(this.target.x + dir.x * 80, dir.y * 80, this.target.y + dir.z * 80);
    cam.up.set(0, 1, 0);
    cam.lookAt(this.target.x, 0, this.target.y);
    cam.updateMatrixWorld(true);
    const view = cam.matrixWorldInverse;
    const lo = new Vector3(Infinity, Infinity, Infinity);
    const hi = new Vector3(-Infinity, -Infinity, -Infinity);
    const p = new Vector3();
    for (const [x, y] of [
      [0, 0],
      [this.devW, 0],
      [0, this.devH],
      [this.devW, this.devH],
    ] as const) {
      for (const h of [-DEPTH, 0, 3.6]) {
        const g = this.groundAt(x, y, h);
        p.set(g.x, h, g.y).applyMatrix4(view);
        lo.min(p);
        hi.max(p);
      }
    }
    cam.left = lo.x - 1;
    cam.right = hi.x + 1;
    cam.bottom = lo.y - 1;
    cam.top = hi.y + 1;
    cam.near = -hi.z - 6;
    cam.far = -lo.z + 6;
    cam.updateProjectionMatrix();
    this.shadowUniforms.uShadowMatrix.value.multiplyMatrices(cam.projectionMatrix, view);

    const swapped: [Mesh, RawShaderMaterial][] = [];
    if (this.columns) {
      swapped.push([this.columns, this.columns.material as RawShaderMaterial]);
      this.columns.material = this.shadowColumn;
    }
    for (const batch of this.batches.values()) {
      swapped.push([batch.mesh, batch.mesh.material as RawShaderMaterial]);
      batch.mesh.material = this.shadowModel;
    }
    this.overlay.visible = false;
    this.gnats.visible = false;
    this.fish.visible = false;
    const gl = this.gl;
    gl.setRenderTarget(this.shadowTarget);
    gl.setClearColor(0x000000, 0);
    gl.clear(true, true, true);
    gl.render(this.worldScene, cam);
    for (const [mesh, material] of swapped) mesh.material = material;
    this.overlay.visible = true;
    this.gnats.visible = true;
    this.fish.visible = true;
  }

  // Markers.

  private setMarkerCapacity(n: number): void {
    this.markerBox = new InstancedBufferAttribute(new Float32Array(4 * n), 4);
    this.markerUv = new InstancedBufferAttribute(new Float32Array(4 * n), 4);
    this.markerMode = new InstancedBufferAttribute(new Float32Array(n), 1);
    this.markerFade = new InstancedBufferAttribute(new Float32Array(n), 1);
    this.markerDepth = new InstancedBufferAttribute(new Float32Array(3 * n), 3);
    this.markerGeometry.setAttribute("iDepth", this.markerDepth);
    this.markerGeometry.setAttribute("iBox", this.markerBox);
    this.markerGeometry.setAttribute("iUv", this.markerUv);
    this.markerGeometry.setAttribute("iMode", this.markerMode);
    this.markerGeometry.setAttribute("iFade", this.markerFade);
    forgetInstanceCount(this.markerGeometry);
  }

  /**
   * How far a point of the plateau lies from ground where a shadow can fall, in tiles: from the
   * nearest tile within two of it that has a top and a weight below 0.4, measured to the edge of
   * that tile's square. 0 on such ground, 3 when there is none near.
   */
  private offGround(world: WorldSource, x: number, y: number, now: number): number {
    let best = 3;
    const cx = Math.round(x);
    const cy = Math.round(y);
    for (let ty = cy - 2; ty <= cy + 2; ty += 1) {
      for (let tx = cx - 2; tx <= cx + 2; tx += 1) {
        if (!hasTop(world, tx, ty) || this.fog.weight(world, tx, ty, now, false) >= 0.4) continue;
        const dx = Math.max(0, Math.abs(x - tx) - 0.5);
        const dy = Math.max(0, Math.abs(y - ty) - 0.5);
        best = Math.min(best, Math.hypot(dx, dy));
      }
    }
    return best;
  }

  private offscreen(p: { x: number; y: number }, margin: number): boolean {
    return p.x < -margin || p.y < -margin || p.x > this.devW + margin || p.y > this.devH + margin;
  }

  /**
   * Life: places the gnats in the world, where what stands in front hides them, and
   * returns the birds and their shadows, drawn in the marker pass before the digits so numbers
   * and banners stay on top: the shadows on the ground first, then the birds in the air.
   */
  private updateLife(world: WorldSource, now: number): Quad[] {
    const birds: Quad[] = [];
    const shadows: Quad[] = [];
    const t = now / 1000;
    const s = this.zoom;
    const p = new Vector3();

    // Gnats: each darts around the swarm's centre on three axes at its own pace, most of all up
    // and down, and the swarm itself drifts slowly. A gnat is a round dot of ink.
    const gnats: number[] = [];
    for (const g of this.scene?.swarms ?? []) {
      const w = this.fog.weight(world, g.tx, g.ty, now, false);
      if (w >= 0.4) continue;
      const sink = DEPTH * w;
      if (this.offscreen(this.project(p.set(g.x, g.h - sink, g.y)), 80)) continue;
      const beat = mix32(g.tx, g.ty, SALT_GNAT) / 4294967296;
      const cx = g.x + 0.06 * Math.sin(0.4 * t + TAU * beat);
      const cy = g.h - sink + 0.05 * Math.sin(0.55 * t + 2 * TAU * beat);
      const cz = g.y + 0.06 * Math.cos(0.33 * t + 3 * TAU * beat);
      for (let j = 0; j < g.n; j += 1) {
        const r = (k: number) => mix32(g.tx * 64 + j, g.ty, SALT_GNAT + k) / 4294967296;
        const pace = 0.8 + 0.45 * r(0);
        gnats.push(
          cx + 0.1 * Math.sin(2.1 * pace * t + TAU * r(1)) + 0.035 * Math.sin(7.3 * t + TAU * r(2)),
          cy + 0.14 * Math.sin(1.3 * pace * t + TAU * r(3)) + 0.04 * Math.sin(8.9 * t + TAU * r(4)),
          cz + 0.1 * Math.sin(1.7 * pace * t + TAU * r(5)) + 0.035 * Math.sin(6.1 * t + TAU * r(6)),
        );
      }
    }
    if (gnats.length > this.gnatPos.array.length) {
      this.gnatPos = new InstancedBufferAttribute(new Float32Array(gnats.length * 2), 3);
      this.gnats.geometry.setAttribute("iPos", this.gnatPos);
      forgetInstanceCount(this.gnats.geometry);
    }
    (this.gnatPos.array as Float32Array).set(gnats);
    this.gnatPos.needsUpdate = true;
    this.gnats.geometry.instanceCount = gnats.length / 3;
    this.gnatUniforms.uSize.value = Math.round(clamp(1.3 * s, 2, 5));

    // Fish: now and then one leaps out of the water along a low arc, its nose along its path,
    // and falls back in. Small rings spread where it left the water, larger ones where it fell
    // back in.
    const leaping: number[] = [];
    const splashes = this.splashUniforms.uSplashes.value;
    let splashCount = 0;
    const splash = (x: number, z: number, age: number, strength: number) => {
      if (splashCount >= MAX_SPLASHES) return;
      splashes[splashCount]?.set(x, z, age, strength);
      splashCount += 1;
    };
    for (const f of this.scene?.fish ?? []) {
      const since = ((((t / f.period + f.phase) % 1) + 1) % 1) * f.period;
      if (since > FISH_LEAP + FISH_RINGS) continue;
      const w = this.fog.weight(world, f.tx, f.ty, now, false);
      if (w >= 0.4) continue;
      const level = f.h - DEPTH * w;
      if (this.offscreen(this.project(p.set(f.x, level, f.y)), 150)) continue;
      const hx = Math.cos(f.heading) * FISH_REACH;
      const hz = Math.sin(f.heading) * FISH_REACH;
      const sx = f.x - hx / 2;
      const sz = f.y - hz / 2;
      if (since < FISH_RINGS) splash(sx, sz, since, 0.6);
      if (since >= FISH_LEAP) splash(sx + hx, sz + hz, since - FISH_LEAP, 1);
      if (since >= FISH_LEAP) continue;
      // It starts and ends a little under the surface, so the water hides it going out and in.
      const u = since / FISH_LEAP;
      const rise = FISH_HEIGHT + 0.04;
      const at = new Vector3(sx + hx * u, level - 0.04 + 4 * rise * u * (1 - u), sz + hz * u);
      const a = this.project(at);
      const b = this.project(
        p.set(at.x + hx * 0.02, at.y + 4 * rise * (1 - 2 * u) * 0.02, at.z + hz * 0.02),
      );
      leaping.push(at.x, at.y, at.z, Math.atan2(a.y - b.y, b.x - a.x));
    }
    // Ducks: rings spread from where a duck was when they started, on the path of its swim in the
    // model shader (FLAG.dabble): a calm set every DUCK_CALM seconds, a trail as it swims, and a
    // set at full strength when it upends and when it comes up again.
    for (const d of this.scene?.ducks ?? []) {
      const w = this.fog.weight(world, d.tx, d.ty, now, false);
      if (w >= 0.4) continue;
      if (this.offscreen(this.project(p.set(d.x, d.h - DEPTH * w, d.y)), 150)) continue;
      const { beat, period } = duckBeat(d.x, d.y);
      const cycle = t / period + beat;
      // The last time the cycle passed the share `at`, and the rings from there if still showing.
      const event = (at: number, strength: number) => {
        const since = fract(cycle - at) * period;
        if (since >= FISH_RINGS) return;
        const from = duckAt(d, t - since);
        splash(from.x, from.y, since, strength);
      };
      event(0.78, 1);
      event(0.94, 1);
      const calm = (t + beat * DUCK_CALM) % DUCK_CALM;
      for (let age = calm; age < FISH_RINGS; age += DUCK_CALM) {
        const from = duckAt(d, t - age);
        splash(from.x, from.y, age, DUCK_CALM_STRENGTH);
      }
    }
    this.splashUniforms.uSplashCount.value = splashCount;
    if (leaping.length > this.fishAt.array.length) {
      this.fishAt = new InstancedBufferAttribute(new Float32Array(leaping.length * 2), 4);
      this.fish.geometry.setAttribute("iFish", this.fishAt);
      forgetInstanceCount(this.fish.geometry);
    }
    (this.fishAt.array as Float32Array).set(leaping);
    this.fishAt.needsUpdate = true;
    this.fish.geometry.instanceCount = leaping.length / 4;
    this.fishUniforms.uLength.value = FISH_LENGTH * ART_PER_TILE * s;

    // Birds: a flock of one to five in a loose wedge behind its leader, each flapping a few beats
    // and gliding, at its own height. Its shadow falls along the shadow sun onto the ground, where
    // the ground is shown, never on the mist.
    const duration = FLIGHT_LENGTH / FLIGHT_SPEED;
    const flocks: Flock[] = [];
    const last = Math.floor(t / FLIGHT_SLOT);
    const first = Math.floor((t - duration) / FLIGHT_SLOT) - 1;
    for (const k of this.flights.keys()) if (k < first) this.flights.delete(k);
    const sun = this.shadowUniforms.uShadowDir.value;
    for (let k = first; k <= last; k += 1) {
      const h = mix32(k, 0, SALT_FLIGHT);
      if ((h & 1023) / 1024 >= FLIGHT_CHANCE) continue;
      const elapsed = t - (k + (((h >>> 10) & 255) / 256) * 0.5) * FLIGHT_SLOT;
      if (elapsed < 0 || elapsed > duration) continue;
      let from = this.flights.get(k);
      if (!from) {
        from = this.target.clone();
        this.flights.set(k, from);
      }
      const angle = (((h >>> 18) & 1023) / 1024) * TAU;
      const dx = Math.cos(angle);
      const dz = Math.sin(angle);
      const along = elapsed * FLIGHT_SPEED - FLIGHT_LENGTH / 2;
      const aside = (((h >>> 28) & 15) / 16 - 0.5) * 10;
      const count = 1 + (mix32(k, 1, SALT_FLIGHT) % 5);
      for (let i = 0; i < count; i += 1) {
        const r = (q: number) => mix32(k, 2 + 8 * i + q, SALT_FLIGHT) / 4294967296;
        const back = 0.7 * i + 0.3 * r(0);
        const wing = i === 0 ? 0 : (i % 2 ? 1 : -1) * 0.5 * Math.ceil(i / 2);
        const side = aside + wing + (r(1) - 0.5) * 0.3 + 0.12 * Math.sin(0.7 * t + TAU * r(2));
        const bx = from.x + dx * (along - back) - dz * side;
        const bz = from.y + dz * (along - back) + dx * side;
        const by = 2.3 + 0.6 * r(3) + 0.08 * Math.sin(0.9 * t + TAU * r(4));
        // A few beats, then a glide with the wings a little raised.
        const cycle = (t / (3.4 + r(5)) + r(6)) % 1;
        const flapping = smoothstep(0, 0.08, cycle) * (1 - smoothstep(0.37, 0.45, cycle));
        const beat = Math.sin(TAU * 2.6 * t + TAU * r(7));
        const pose = ((0.18 + (beat - 0.18) * flapping + 1) / 2) * 0.98;
        const radius = 6.5 * (0.85 + 0.3 * r(0));
        const gx = bx - (sun.x / sun.y) * by;
        const gz = bz - (sun.z / sun.y) * by;
        const q = this.project(p.set(bx, by, bz));
        if (!this.offscreen(q, radius * s)) {
          // Over the mist it dissolves, but only once its shadow has left the ground, so a
          // shadow never shows without its bird.
          const fade = 1 - smoothstep(0.4, 1.6, this.offGround(world, gx, gz, now));
          if (fade > 0.01) {
            birds.push({ name: "bird", x: q.x, y: q.y, mode: 6 + pose, r: radius, fade });
            const period = 3.4 + r(5);
            if (i === 0) flocks.push({ k, x: bx, y: bz, h: by, beat: t + (1 - cycle) * period });
          }
        }
        const tx = Math.round(gx);
        const ty = Math.round(gz);
        if (!hasTop(world, tx, ty)) continue;
        const w = this.fog.weight(world, tx, ty, now, false);
        if (w >= 0.4) continue;
        const level = world.terrain(tx, ty) === Terrain.water ? -WATER_DROP : 0;
        const g = this.project(p.set(gx, level - DEPTH * w, gz));
        if (this.offscreen(g, radius * s)) continue;
        // Smaller than the bird, and as flat as the ground looks from the camera.
        shadows.push({
          name: "bird-shadow",
          x: g.x,
          y: g.y,
          mode: 7 + pose,
          r: BIRD_SHADOW * radius,
          flat: Math.sin(this.pitch),
        });
      }
    }
    this.flocksDrawn = flocks;
    return [...shadows, ...birds];
  }

  /**
   * No life: the gnats, the leaping fish and the rings on the water are drawn no more, which the
   * counts of the last frame with life would otherwise keep, frozen, when reduced motion is turned
   * on while the game runs (found by the Metal port, 2026-10-20). Birds are quads and simply stop.
   */
  private stillLife(): Quad[] {
    this.flocksDrawn = [];
    this.gnats.geometry.instanceCount = 0;
    this.fish.geometry.instanceCount = 0;
    this.splashUniforms.uSplashCount.value = 0;
    return [];
  }

  private updateMarkers(world: WorldSource, now: number): void {
    const quads: Quad[] = this.life ? this.updateLife(world, now) : this.stillLife();
    // Only the birds and their shadows on the screen are among the quads.
    this.flying = quads.length > 0;
    const scene = this.scene;
    const s = this.zoom;
    const shrink = this.numberScale();
    // Decision 0010: the depth of a marker's anchor, moved toward the camera so the ground and the
    // tufts of its own tile never hide it while what stands on the tiles in front does. A number
    // lies flat, so the ground in front of its lower half must stay behind it: the lower the
    // camera, the further it moves. A banner stands up, its depth falling up the pole.
    const look = this.camera.getWorldDirection(new Vector3());
    const range = this.camera.far - this.camera.near;
    const depthAt = (x: number, h: number, y: number, toward: number): number | undefined => {
      if (!this.markersBehind) return undefined;
      const v = new Vector3(x, h, y).addScaledVector(look, -toward).project(this.camera);
      return v.z * 0.5 + 0.5;
    };
    const flatToward = 0.25 + 0.2 / Math.tan(this.pitch);
    const standing = Math.tan(this.pitch) / (s * ART_PER_TILE * range);
    const foot = this.markersBehind ? 0 : 8 * s;
    if (scene) {
      for (const m of scene.markers) {
        if (m.kind === "digit") {
          // Below a third of their size they are crumbs, not numbers.
          if (shrink < 0.3) continue;
          const w = this.fog.weight(world, m.x, m.y, now, this.reducedMotion);
          if (w >= 0.4) continue;
          const p = this.project(new Vector3(m.x, -DEPTH * w, m.y));
          const size = DIGIT_SIZE * shrink;
          const depth = depthAt(m.x, -DEPTH * w, m.y, flatToward);
          const at = { x: p.x, y: p.y, size, ...(depth === undefined ? {} : { depth }) };
          quads.push({ name: `digit-${m.n}-halo`, mode: 3, ...at });
          quads.push({ name: `digit-${m.n}`, mode: 2, ...at });
        } else if (m.kind === "banner") {
          const p = this.project(new Vector3(m.x, 0, m.y));
          const depth = depthAt(m.x, 0, m.y, 0.2);
          const at = {
            x: p.x,
            y: p.y + foot,
            ...(depth === undefined ? {} : { depth, rise: standing }),
          };
          quads.push({ name: "banner-pole", mode: 2, ...at });
          quads.push({ name: "banner-flag", mode: 4, ...at });
        } else {
          const p = this.project(new Vector3(m.x, 0, m.y));
          const depth = depthAt(m.x, 0, m.y, flatToward);
          quads.push({
            name: "wrong-x",
            x: p.x,
            y: p.y,
            mode: 4,
            ...(depth === undefined ? {} : { depth }),
          });
        }
      }
    }
    if (quads.length > this.markerMode.count) this.setMarkerCapacity(quads.length * 2);
    const { unit, width, height, sprites } = this.atlas;
    quads.forEach((q, i) => {
      if (q.r !== undefined) {
        const r = q.r * s;
        const rh = r * (q.flat ?? 1);
        this.markerBox.setXYZW(i, q.x - r, q.y - rh, 2 * r, 2 * rh);
        this.markerUv.setXYZW(i, 0, 0, 1, 1);
        this.markerMode.setX(i, q.mode);
        this.markerFade.setX(i, q.fade ?? 1);
        this.markerDepth.setXYZ(i, -1, 0, 0);
        return;
      }
      const sp = sprites[q.name];
      if (!sp) return;
      const k = (q.size ?? 1) * s;
      this.markerBox.setXYZW(
        i,
        q.x - (sp.ax / unit) * k,
        q.y - (sp.ay / unit) * k,
        (sp.w / unit) * k,
        (sp.h / unit) * k,
      );
      this.markerUv.setXYZW(
        i,
        sp.x / width,
        sp.y / height,
        (sp.x + sp.w) / width,
        (sp.y + sp.h) / height,
      );
      this.markerMode.setX(i, q.mode);
      this.markerFade.setX(i, 1);
      this.markerDepth.setXYZ(i, q.depth ?? -1, q.rise ?? 0, q.y);
    });
    this.markerBox.needsUpdate = true;
    this.markerUv.needsUpdate = true;
    this.markerMode.needsUpdate = true;
    this.markerFade.needsUpdate = true;
    this.markerDepth.needsUpdate = true;
    this.markerGeometry.instanceCount = quads.length;
  }

  // Frame.

  /**
   * The keyboard cursor, a dashed square on the tile, and the rim of the chasm that ended the run,
   * a solid square just inside its edges. Rebuilt only when either changes.
   */
  private updateOverlay(world: WorldSource, cursor: Coord | null): void {
    const killer = world.status === "dead" ? world.killer : null;
    const key = `${cursor?.x},${cursor?.y},${killer?.x},${killer?.y}`;
    if (key === this.overlayKey) return;
    this.overlayKey = key;
    const pos: number[] = [];
    const kind: number[] = [];
    const h = 0.004;
    const rect = (x0: number, z0: number, x1: number, z1: number, k: number) => {
      for (const [x, z] of [
        [x0, z0],
        [x1, z0],
        [x1, z1],
        [x0, z0],
        [x1, z1],
        [x0, z1],
      ] as const) {
        pos.push(x, h, z);
        kind.push(k);
      }
    };
    /** A square frame around tile (tx, ty), `w` wide, inside its edges, in `dashes` per side. */
    const frame = (tx: number, ty: number, w: number, dashes: number, k: number) => {
      const lo = -0.5;
      const step = 1 / dashes;
      const len = dashes === 1 ? 1 : step * 0.6;
      for (let d = 0; d < dashes; d += 1) {
        const a = lo + d * step + (dashes === 1 ? 0 : step * 0.2);
        const b = a + len;
        rect(tx + a, ty + lo, tx + b, ty + lo + w, k);
        rect(tx + a, ty - lo - w, tx + b, ty - lo, k);
        rect(tx + lo, ty + a, tx + lo + w, ty + b, k);
        rect(tx - lo - w, ty + a, tx - lo, ty + b, k);
      }
    };
    if (killer && this.killerRim) frame(killer.x, killer.y, 0.08, 1, 1);
    if (cursor) frame(cursor.x, cursor.y, 0.06, 4, 2);
    const g = new BufferGeometry();
    g.setAttribute("position", new Float32BufferAttribute(pos, 3));
    g.setAttribute("aKind", new Float32BufferAttribute(kind, 1));
    this.overlay.geometry.dispose();
    this.overlay.geometry = g;
  }

  render(world: WorldSource, now: number, cursor: Coord | null): FrameStats {
    const t0 = performance.now();
    const dt = Math.min(50, this.lastFrame ? now - this.lastFrame : 16);
    this.lastFrame = now;
    if (this.gliding) {
      this.target.x += this.glide.x * dt;
      this.target.y += this.glide.y * dt;
      this.glide.multiplyScalar(Math.exp(-GLIDE_DECAY * dt));
      if (this.glide.length() < 0.0002) this.gliding = false;
      this.updateCamera();
    }
    // A new window when the camera has wandered a quarter of the way out of it, or sees more
    // than it covers, or far less.
    const needed = this.neededWindow();
    const off =
      Math.abs(this.target.x - (this.window.x + this.size / 2)) > this.size / 4 ||
      Math.abs(this.target.y - (this.window.y + this.size / 2)) > this.size / 4;
    const resize = needed > this.size || needed < this.size / 2;
    let composeMs = 0;
    if (this.dirty || off || resize || !this.scene) {
      this.rebuild(world, resize ? needed : this.size, now);
      composeMs = performance.now() - t0;
    }
    this.updateMist(world, now);
    this.updateMarkers(world, now);
    this.updateOverlay(world, cursor);

    const t1 = performance.now();
    const theme = this.theme;
    const paper = hexToVec(theme.paper);
    const ink = hexToVec(theme.ink);
    const shade = hexToVec(theme.shade ?? theme.ink);
    const lines = hexToVec(theme.line ?? theme.ink);
    const accent = hexToVec(theme.accent);
    const u = this.postUniforms;
    u.uViewport.value.set(this.devW, this.devH);
    const viewProj = new Matrix4().multiplyMatrices(
      this.camera.projectionMatrix,
      this.camera.matrixWorldInverse,
    );
    u.uInvViewProj.value.copy(viewProj).invert();
    this.camera.getWorldDirection(u.uCamDir.value);
    u.uWinOrigin.value.set(this.window.x, this.window.y);
    u.uTime.value = this.reducedMotion ? 0 : now / 1000;
    this.windTime.value = u.uTime.value;
    this.camYaw.value = this.yaw;
    u.uDot.value = this.dot;
    // Zoomed out, the finer screen: the blend runs from none to all between the two scales.
    const fine = this.fineDots;
    if (fine) {
      const k = Math.min(1, Math.max(0, (fine.from - this.zoom) / (fine.from - fine.to)));
      u.uFine.value = k * k * (3 - 2 * k);
    } else {
      u.uFine.value = 0;
    }
    u.uLine.value = Math.max(1, Math.min(4, Math.round(0.85 * this.zoom)));
    u.uPaper.value.copy(paper);
    u.uInk.value.copy(ink);
    u.uShade.value.copy(shade);
    u.uLineColour.value.copy(lines);
    u.uAccent.value.copy(accent);
    if (this.bare) u.uFogParams.value.set(0, 0, 0);
    else u.uFogParams.value.set(theme.fog.base, theme.fog.clouds, theme.fog.shadow);
    this.glow.value = theme.glow ?? GLOW_TONE;
    if (this.lock === "world") {
      // The dots follow the world: they move with the ground's origin and turn with the world,
      // measured by the direction that runs straight across the screen in the flat view.
      const o = this.project(new Vector3(0, 0, 0));
      const e = this.project(new Vector3(1, 0, -1));
      const angle = Math.atan2(-(e.y - o.y), e.x - o.x);
      u.uAnchor.value.set(o.x, this.devH - o.y);
      u.uTurn.value.set(Math.cos(angle), Math.sin(angle));
    } else {
      u.uAnchor.value.set(0, 0);
      u.uTurn.value.set(1, 0);
    }
    const mu = this.markerUniforms;
    mu.uViewport.value.set(this.devW, this.devH);
    this.gnatUniforms.uViewport.value.set(this.devW, this.devH);
    this.fishUniforms.uViewport.value.set(this.devW, this.devH);
    mu.uPaper.value.copy(paper);
    mu.uInk.value.copy(ink);
    mu.uShade.value.copy(shade);
    mu.uAccent.value.copy(accent);

    const gl = this.gl;
    if (this.shadowsOn) this.renderShadows();
    gl.setRenderTarget(this.gbuffer);
    gl.setClearColor(0x000000, 0);
    gl.clear(true, true, true);
    gl.render(this.worldScene, this.camera);
    if (this.tilt > 0) this.renderTilted();
    else {
      gl.setRenderTarget(null);
      gl.clear(true, true, true);
      gl.render(this.postScene, this.screenCamera);
    }
    gl.render(this.markerScene, this.screenCamera);
    const drawMs = performance.now() - t1;
    return {
      composeMs,
      drawMs,
      animating: this.fog.animating || this.gliding,
      quick: this.life && this.flying,
    };
  }

  /**
   * Tilt-shift, a trial (Daniel, 2026-10-20): from 0, off, to 1, the strongest. The band across
   * the screen's middle stays sharp; above and below it the world goes soft before the halftone.
   */
  setTiltShift(amount: number): void {
    this.tilt = Math.min(1, Math.max(0, amount));
    this.invalidate();
  }

  /**
   * The tone of the last frame drawn, before the halftone and before the tilt-shift's blur, as the
   * tilt pass's first step writes it (decision 0020): per device pixel, red the tone, green how
   * much of the darkness is ink, blue the accent, rows from the bottom. For the reference frames
   * other renderers are checked against; call it right after a frame is drawn.
   */
  toneFrame(): { width: number; height: number; data: Uint8Array } {
    const { target, prepass } = this.ensureTilt();
    const gl = this.gl;
    this.postMesh.material = prepass;
    gl.setRenderTarget(target);
    gl.clear(true, true, true);
    gl.render(this.postScene, this.screenCamera);
    const data = new Uint8Array(this.devW * this.devH * 4);
    gl.readRenderTargetPixels(target, 0, 0, this.devW, this.devH, data);
    gl.setRenderTarget(null);
    this.postMesh.material = this.postMaterial;
    return { width: this.devW, height: this.devH, data };
  }

  /** The tilt pass's target and its two materials, made the first time they are needed. */
  private ensureTilt(): {
    target: WebGLRenderTarget;
    prepass: RawShaderMaterial;
    material: RawShaderMaterial;
  } {
    if (!this.tiltTarget || !this.tiltPrepass || !this.tiltMaterial) {
      this.tiltTarget = new WebGLRenderTarget(this.devW, this.devH, {
        type: UnsignedByteType,
        minFilter: NearestFilter,
        magFilter: NearestFilter,
        depthBuffer: false,
        generateMipmaps: false,
      });
      this.tiltPrepass = new RawShaderMaterial({
        glslVersion: GLSL3,
        vertexShader: POST_VERTEX,
        fragmentShader: POST_FRAGMENT,
        defines: { TILT_PREPASS: 1 },
        depthTest: false,
        depthWrite: false,
        uniforms: this.postUniforms,
      });
      const u = this.postUniforms;
      this.tiltMaterial = new RawShaderMaterial({
        glslVersion: GLSL3,
        vertexShader: POST_VERTEX,
        fragmentShader: TILT_FRAGMENT,
        depthTest: false,
        depthWrite: false,
        uniforms: {
          uTilt: { value: this.tiltTarget.texture },
          uViewport: u.uViewport,
          uBlur: { value: 0 },
          uTaps: { value: TILT_TAPS },
          uFocus: { value: new Vector2(0.5, 0.12) },
          uDot: u.uDot,
          uFine: u.uFine,
          uAnchor: u.uAnchor,
          uTurn: u.uTurn,
          uPaper: u.uPaper,
          uInk: u.uInk,
          uShade: u.uShade,
          uLineColour: u.uLineColour,
          uAccent: u.uAccent,
        },
      });
    }
    return { target: this.tiltTarget, prepass: this.tiltPrepass, material: this.tiltMaterial };
  }

  /** The post pass into the tilt target, then the tilt pass, which blurs it, to the screen. */
  private renderTilted(): void {
    const gl = this.gl;
    const { target, prepass, material } = this.ensureTilt();
    // At its strongest the blur reaches a fiftieth of the screen's height at its top and bottom.
    const blur = material.uniforms.uBlur;
    if (blur) blur.value = this.tilt * 0.02 * this.devH;
    this.postMesh.material = prepass;
    gl.setRenderTarget(target);
    gl.clear(true, true, true);
    gl.render(this.postScene, this.screenCamera);
    this.postMesh.material = material;
    gl.setRenderTarget(null);
    gl.clear(true, true, true);
    gl.render(this.postScene, this.screenCamera);
    this.postMesh.material = this.postMaterial;
  }

  dispose(): void {
    this.gl.dispose();
    this.gbuffer.dispose();
    this.tiltTarget?.dispose();
  }
}

/**
 * The tilt-shift's 24 taps along a golden-angle spiral (visual spec, Tilt-shift): tap j lies
 * r sqrt((j + 0.5) / 24) (cos a, sin a) from the pixel, a = 2.3999632 (j + 0.5), with the angle
 * and the root in single precision and the cosine and sine rounded from doubles, as the Metal
 * world works them out. Once here instead of 72 sines, cosines and roots at every blurred pixel.
 */
const TILT_TAPS: readonly Vector4[] = Array.from({ length: 24 }, (_, j) => {
  const share = j + 0.5;
  const angle = Math.fround(share * Math.fround(2.3999632));
  return new Vector4(Math.cos(angle), Math.sin(angle), Math.sqrt(Math.fround(share / 24)), 0);
});

/**
 * three.js keeps an instanced geometry's largest instance count from the first time it is drawn
 * and never grows it, so after its attributes grow the count must be forgotten, or every instance
 * past the first capacity goes undrawn. Found by the Metal port (2026-10-20): past 256 markers
 * the last numbers went missing, past 256 gnats and 32 fish the last of those.
 */
function forgetInstanceCount(geometry: BufferGeometry): void {
  delete (geometry as unknown as { _maxInstanceCount?: number })._maxInstanceCount;
}

function makePostUniforms(
  gbuffer: WebGLRenderTarget,
  weights: DataTexture,
  near: DataTexture,
  noise: DataTexture,
  options: { readonly reducedMotion: boolean; readonly dot: number },
) {
  return {
    uScene: { value: gbuffer.textures[0] },
    uIds: { value: gbuffer.textures[1] },
    uWeights: { value: weights },
    uNear: { value: near },
    uNoise: { value: noise },
    uViewport: { value: new Vector2(1, 1) },
    uInvViewProj: { value: new Matrix4() },
    uCamDir: { value: new Vector3() },
    uDepth: { value: DEPTH },
    uWinOrigin: { value: new Vector2() },
    uWinSize: { value: MIN_WINDOW },
    uArt: { value: ART_PER_TILE },
    uTime: { value: 0 },
    uDrift: { value: options.reducedMotion ? 0 : 1 },
    uDot: { value: options.dot },
    uFine: { value: 0 },
    uLine: { value: 2 },
    uAnchor: { value: new Vector2() },
    uTurn: { value: new Vector2(1, 0) },
    uPaper: { value: new Vector3() },
    uInk: { value: new Vector3() },
    uShade: { value: new Vector3() },
    uLineColour: { value: new Vector3() },
    uAccent: { value: new Vector3() },
    uFogParams: { value: new Vector3() },
  };
}

function makeMarkerUniforms(atlas: Texture, depth: DepthTexture | null) {
  return {
    uAtlas: { value: atlas },
    uSceneDepth: { value: depth },
    uViewport: { value: new Vector2(1, 1) },
    uPaper: { value: new Vector3() },
    uInk: { value: new Vector3() },
    uShade: { value: new Vector3() },
    uAccent: { value: new Vector3() },
  };
}

/**
 * The outline id base of an instance, 0 to 4095, from its tile and its place among the tile's
 * instances, so neighbouring models and two of the same model on one tile get lines between them.
 */
function instanceId(tx: number, ty: number, slot: number): number {
  return (mix32(tx, ty, 0x69647332) + Math.imul(slot, 977)) & 4095;
}

/**
 * The outline id base that every tile of a family that joins shares, so neighbouring fields of a
 * kind draw no line between them: from the family's place k in the catalog, mix32(k, 0, "fiel").
 */
function joinedId(family: number): number {
  return mix32(family, 0, 0x6669656c) & 4095;
}

function smoothstep(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
